import React from 'react';
import { Box, Checkbox, Typography } from '@mui/material';
import { radius } from '../../themes/smartinventoryTheme';

// Below the sm breakpoint the DataGrid's columns can't fit without
// horizontal scroll, so each row renders as a stacked card instead — driven
// off the same `columns` array every page already defines, so nothing here
// is page-specific. The first column is the card's header (usually a
// thumbnail + title renderCell); an `actions` field renders top-right;
// everything else lists as label/value pairs. Pagination (if any) is owned
// by DataTableCard, rendered below this list.
const DataTableCardMobile = ({
  rows, columns, getRowId, checkboxSelection, rowSelectionModel = [], onRowSelectionModelChange,
  onRowClick,
}) => {
  const [primaryCol, ...restCols] = columns;
  const actionsCol = restCols.find((c) => c.field === 'actions');
  const detailCols = restCols.filter((c) => c.field !== 'actions' && c.headerName);

  const cellValue = (col, row, id) => {
    if (!col.renderCell) return row[col.field];
    return col.renderCell({ row, value: row[col.field], id });
  };

  const toggleSelection = (id) => {
    if (!onRowSelectionModelChange) return;
    const next = rowSelectionModel.includes(id)
      ? rowSelectionModel.filter((x) => x !== id)
      : [...rowSelectionModel, id];
    onRowSelectionModelChange(next);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      {rows.map((row) => {
        const id = getRowId ? getRowId(row) : row.id;
        return (
          <Box
            key={id}
            onClick={(event) => onRowClick?.({ row, id }, event)}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1.25,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: `${radius.lg}px`,
              bgcolor: 'background.paper',
              p: 1.5,
              cursor: onRowClick ? 'pointer' : 'default',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              {checkboxSelection && (
                // Padded up to the 44px minimum mobile tap target (the
                // theme's own Checkbox padding is sized for desktop density).
                <Checkbox
                  className="MuiCheckbox-root"
                  checked={rowSelectionModel.includes(id)}
                  onClick={(event) => { event.stopPropagation(); toggleSelection(id); }}
                  sx={{ p: 3, m: -2.5 }}
                />
              )}
              <Box sx={{ flex: 1, minWidth: 0 }}>{cellValue(primaryCol, row, id)}</Box>
              {actionsCol && (
                // Row-action buttons reuse the same compact desktop renderCell
                // (an IconButton size="small", ~34px) — this forces the mobile
                // tap target up to the 44px minimum without touching the
                // shared column definition or its desktop rendering.
                <Box sx={{ flexShrink: 0, '& .MuiIconButton-root': { minWidth: 44, minHeight: 44 } }}>
                  {cellValue(actionsCol, row, id)}
                </Box>
              )}
            </Box>
            {detailCols.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {detailCols.map((col) => (
                  <Box key={col.field} sx={{ minWidth: 72 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {col.headerName}
                    </Typography>
                    {cellValue(col, row, id)}
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default DataTableCardMobile;
